import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { doc, onSnapshot, Unsubscribe, collection, query, where } from 'firebase/firestore';
import { db } from '../services/firebase/config';
import { UnitStatus } from '../types/property.types';

interface UnitStatusUpdate {
  developmentId: string;
  unitId: string;
  status: UnitStatus;
  updatedAt: Date;
}

interface StatusContextType {
  unitStatuses: Map<string, UnitStatus>;
  dealStatuses: Map<string, any>;
  subscribeToUnit: (developmentId: string, unitId: string) => void;
  unsubscribeFromUnit: (developmentId: string, unitId: string) => void;
  subscribeToDeal: (unitId: string) => void;
  unsubscribeToDeal: (unitId: string) => void;
  getUnitStatus: (developmentId: string, unitId: string) => UnitStatus | undefined;
  getDealStatus: (unitId: string) => any;
}

const StatusContext = createContext<StatusContextType | undefined>(undefined);

export const useStatus = () => {
  const context = useContext(StatusContext);
  if (!context) {
    throw new Error('useStatus must be used within a StatusProvider');
  }
  return context;
};

interface StatusProviderProps {
  children: ReactNode;
}

export const StatusProvider: React.FC<StatusProviderProps> = ({ children }) => {
  const [unitStatuses, setUnitStatuses] = useState<Map<string, UnitStatus>>(new Map());
  const [dealStatuses, setDealStatuses] = useState<Map<string, any>>(new Map());
  const [subscriptions, setSubscriptions] = useState<Map<string, Unsubscribe>>(new Map());

  const subscribeToUnit = (developmentId: string, unitId: string) => {
    const key = `${developmentId}-${unitId}`;
    
    // Don't subscribe if already subscribed
    if (subscriptions.has(key)) {
      return;
    }

    // Subscribe to development document to watch for unit status changes
    const unsubscribe = onSnapshot(
      doc(db, 'developments', developmentId),
      (snapshot) => {
        if (snapshot.exists()) {
          const development = snapshot.data();
          const units = development?.units || [];
          const unit = units.find((u: { id: string }) => u.id === unitId);
          
          if (unit) {
            setUnitStatuses(prev => {
              const newMap = new Map(prev);
              newMap.set(key, unit.status);
              return newMap;
            });
          }
        }
      },
      (error) => {
        console.error(`Error listening to unit status for ${key}:`, error);
      }
    );

    setSubscriptions(prev => {
      const newMap = new Map(prev);
      newMap.set(key, unsubscribe);
      return newMap;
    });
  };

  const unsubscribeFromUnit = (developmentId: string, unitId: string) => {
    const key = `${developmentId}-${unitId}`;
    const unsubscribe = subscriptions.get(key);
    
    if (unsubscribe) {
      unsubscribe();
      setSubscriptions(prev => {
        const newMap = new Map(prev);
        newMap.delete(key);
        return newMap;
      });
      
      setUnitStatuses(prev => {
        const newMap = new Map(prev);
        newMap.delete(key);
        return newMap;
      });
    }
  };

  const getUnitStatus = (developmentId: string, unitId: string): UnitStatus | undefined => {
    return unitStatuses.get(`${developmentId}-${unitId}`);
  };
  
  // Subscribe to deal capture changes
  const subscribeToDeal = (unitId: string) => {
    const key = `deal-${unitId}`;
    
    // Don't subscribe if already subscribed
    if (subscriptions.has(key)) {
      return;
    }
    
    // Subscribe to dealCapture document
    const unsubscribe = onSnapshot(
      doc(db, 'dealCaptures', unitId),
      (snapshot) => {
        if (snapshot.exists()) {
          const dealData = snapshot.data();
          setDealStatuses(prev => {
            const newMap = new Map(prev);
            newMap.set(unitId, dealData);
            return newMap;
          });
        } else {
          // Deal doesn't exist, remove from map
          setDealStatuses(prev => {
            const newMap = new Map(prev);
            newMap.delete(unitId);
            return newMap;
          });
        }
      },
      (error) => {
        console.error(`Error listening to deal status for ${unitId}:`, error);
      }
    );
    
    setSubscriptions(prev => {
      const newMap = new Map(prev);
      newMap.set(key, unsubscribe);
      return newMap;
    });
  };
  
  // Unsubscribe from deal changes
  const unsubscribeToDeal = (unitId: string) => {
    const key = `deal-${unitId}`;
    const unsubscribe = subscriptions.get(key);
    
    if (unsubscribe) {
      unsubscribe();
      setSubscriptions(prev => {
        const newMap = new Map(prev);
        newMap.delete(key);
        return newMap;
      });
      
      setDealStatuses(prev => {
        const newMap = new Map(prev);
        newMap.delete(unitId);
        return newMap;
      });
    }
  };
  
  // Get deal status
  const getDealStatus = (unitId: string): any => {
    return dealStatuses.get(unitId);
  };

  // Cleanup all subscriptions on unmount
  useEffect(() => {
    return () => {
      subscriptions.forEach(unsubscribe => unsubscribe());
    };
  }, [subscriptions]);

  return (
    <StatusContext.Provider value={{
      unitStatuses,
      dealStatuses,
      subscribeToUnit,
      unsubscribeFromUnit,
      subscribeToDeal,
      unsubscribeToDeal,
      getUnitStatus,
      getDealStatus
    }}>
      {children}
    </StatusContext.Provider>
  );
};